/**
 * Expo Organizer — Batch B foundations (Feb 2026 · R17).
 * Aggregation dashboard + expo list + global exhibitor/visitor pools.
 * Floor plans, stall sales pipeline UI, and visitor kiosk deferred.
 */
import { useState, useEffect, useCallback } from "react";
import { NavLink, Routes, Route, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import Phase2Panel from "@/pages/app/expo_organizer/Phase2Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutDashboard, Building2, Users, Ticket, Globe2, Plus, TrendingUp, MapPin, ArrowLeft, DollarSign } from "lucide-react";

const STATUS_COLORS = {
  planned: "border-neutral-400 text-neutral-600 bg-neutral-50",
  announced: "border-blue-400 text-blue-600 bg-blue-50",
  selling: "border-amber-400 text-amber-700 bg-amber-50",
  sold_out: "border-purple-400 text-purple-600 bg-purple-50",
  live: "border-emerald-400 text-emerald-700 bg-emerald-50",
  closed: "border-neutral-400 text-neutral-500 bg-neutral-100",
  cancelled: "border-rose-400 text-rose-600 bg-rose-50",
};

const PART_STATUSES = ["enquiry", "quoted", "negotiating", "booked", "paid", "cancelled"];
const REG_STATUSES = ["registered", "confirmed", "attended", "no_show", "cancelled"];

export default function ExpoOrganizer() {
  return (
    <div className="flex flex-col h-full">
      <SubNav />
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="expos" element={<ExposList />} />
          <Route path="expos/:id" element={<ExpoDetail />} />
          <Route path="exhibitors" element={<ExhibitorPool />} />
          <Route path="visitors" element={<VisitorPool />} />
        </Routes>
      </div>
    </div>
  );
}

function SubNav() {
  const links = [
    { to: "/app/expo-organizer", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/app/expo-organizer/expos", icon: Building2, label: "Expos" },
    { to: "/app/expo-organizer/exhibitors", icon: Users, label: "Exhibitors" },
    { to: "/app/expo-organizer/visitors", icon: Ticket, label: "Visitors" },
  ];
  return (
    <div className="border-b bg-neutral-50/60 px-6 py-2 flex items-center gap-1">
      {links.map(l => (
        <NavLink key={l.to} to={l.to} end={l.end} className={({ isActive }) => `px-3 py-1.5 text-xs uppercase tracking-widest flex items-center gap-1 ${isActive ? "border-b-2 border-primary text-primary font-semibold" : "text-neutral-600 hover:text-neutral-900"}`} data-testid={`org-nav-${l.label.toLowerCase()}`}>
          <l.icon className="w-3 h-3" /> {l.label}
        </NavLink>
      ))}
    </div>
  );
}

// ─────────────── Dashboard ───────────────

function Dashboard() {
  const { activeWorkspaceId: ws } = useAuth();
  const [d, setD] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!ws) return;
    api.get(`/workspaces/${ws}/organizer/dashboard`).then(r => setD(r.data));
  }, [ws]);
  if (!d) return <div className="p-6 text-sm text-muted-foreground">Loading dashboard…</div>;
  const t = d.totals;
  return (
    <div className="p-6 space-y-4" data-testid="organizer-dashboard">
      <PageHeader title="Organizer Dashboard" description="Aggregated view across every expo you're organizing — pipeline, pools, and revenue at a glance." />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<Building2 className="w-4 h-4 text-neutral-600" />} label="Expos managed" value={t.expos} onClick={() => navigate("/app/expo-organizer/expos")} />
        <Kpi icon={<Users className="w-4 h-4 text-blue-500" />} label="Exhibitor pool" value={t.exhibitors_pool} onClick={() => navigate("/app/expo-organizer/exhibitors")} />
        <Kpi icon={<Ticket className="w-4 h-4 text-purple-500" />} label="Visitor pool" value={t.visitors_pool} onClick={() => navigate("/app/expo-organizer/visitors")} />
        <Kpi icon={<Globe2 className="w-4 h-4 text-emerald-500" />} label="Countries" value={t.countries_covered} />
        <Kpi icon={<TrendingUp className="w-4 h-4 text-amber-500" />} label="Stalls sold" value={t.stalls_sold} />
        <Kpi icon={<DollarSign className="w-4 h-4 text-emerald-600" />} label="Revenue booked" value={t.revenue_booked.toLocaleString()} />
        <Kpi icon={<Ticket className="w-4 h-4 text-blue-500" />} label="Total registrations" value={t.total_registrations} />
        <Kpi icon={<MapPin className="w-4 h-4 text-rose-500" />} label="Upcoming" value={d.upcoming_expos.length} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Expos by status</div>
          {Object.keys(d.expos_by_status).length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No expos yet.</div>
          ) : (
            <div className="space-y-1">
              {Object.entries(d.expos_by_status).map(([s, count]) => {
                const pct = t.expos ? (100 * count / t.expos) : 0;
                return (
                  <div key={s} className="flex items-center gap-2">
                    <div className="w-24 text-xs capitalize">{s}</div>
                    <div className="flex-1 relative"><div className={`h-4 ${STATUS_COLORS[s]?.split(" ").find(x => x.startsWith("bg-")) || "bg-neutral-200"}`} style={{ width: `${pct}%` }} /></div>
                    <div className="w-10 text-right font-mono text-xs">{count}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="border p-4">
          <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">Upcoming expos</div>
          {d.upcoming_expos.length === 0 ? (
            <div className="text-xs text-muted-foreground italic">No upcoming expos scheduled.</div>
          ) : (
            <div className="space-y-2">
              {d.upcoming_expos.map(e => (
                <button key={e.id} onClick={() => navigate(`/app/expo-organizer/expos/${e.id}`)} className={`w-full text-left border p-2 hover:border-primary ${STATUS_COLORS[e.status] || ""}`} data-testid={`org-upcoming-${e.id}`}>
                  <div className="text-sm font-semibold">{e.name}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {e.city}{e.country_code && `, ${e.country_code}`} · {e.start_date} → {e.end_date}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon, label, value, onClick }) {
  const wrapCls = onClick ? "border p-3 hover:border-primary cursor-pointer transition-colors" : "border p-3";
  return (
    <div className={wrapCls} onClick={onClick}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">{icon}{label}</div>
      <div className="text-2xl font-mono mt-1">{value}</div>
    </div>
  );
}

// ─────────────── Expos List ───────────────

function ExposList() {
  const { activeWorkspaceId: ws } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const load = useCallback(() => {
    if (!ws) return;
    const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
    api.get(`/workspaces/${ws}/organizer/expos${params}`).then(r => setRows(r.data || []));
  }, [ws, q]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-6 space-y-4" data-testid="org-expos-list">
      <PageHeader title="Managed Expos" description="Every expo you're organizing across the world." actions={
        <Button className="rounded-none h-9" onClick={() => setOpenCreate(true)} data-testid="org-expo-new"><Plus className="w-4 h-4 mr-1" /> New expo</Button>
      } />
      <Input placeholder="Search name / city / venue…" value={q} onChange={e => setQ(e.target.value)} className="h-8 rounded-none text-xs max-w-md" />
      {rows.length === 0 ? (
        <div className="border-2 border-dashed p-10 text-center text-sm text-muted-foreground">
          No managed expos yet. Click New expo to start.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {rows.map(e => (
            <button key={e.id} onClick={() => navigate(`/app/expo-organizer/expos/${e.id}`)} className="border p-3 text-left hover:border-primary" data-testid={`org-expo-${e.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3" />{e.city}{e.country_code && `, ${e.country_code}`}</div>
                  <div className="font-semibold truncate">{e.name}</div>
                  <div className="text-[11px] text-muted-foreground">{e.start_date} → {e.end_date}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 border ${STATUS_COLORS[e.status] || ""}`}>{e.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2 text-center text-xs">
                <div className="border p-1"><div className="text-[9px] text-muted-foreground uppercase">Stalls</div><div className="font-mono">{e.stalls_sold}/{e.total_stall_capacity || "—"}</div></div>
                <div className="border p-1"><div className="text-[9px] text-muted-foreground uppercase">Regs</div><div className="font-mono">{e.registrations_count}</div></div>
                <div className="border p-1"><div className="text-[9px] text-muted-foreground uppercase">Expected</div><div className="font-mono">{e.expected_visitors ? (e.expected_visitors / 1000).toFixed(0) + "k" : "—"}</div></div>
              </div>
            </button>
          ))}
        </div>
      )}
      {openCreate && <CreateExpoDialog ws={ws} onClose={() => setOpenCreate(false)} onDone={(id) => { setOpenCreate(false); if (id) navigate(`/app/expo-organizer/expos/${id}`); }} />}
    </div>
  );
}

function CreateExpoDialog({ ws, onClose, onDone }) {
  const [f, setF] = useState({ name: "", city: "", country_code: "IN", venue_name: "", start_date: "", end_date: "", status: "planned", total_stall_capacity: "", expected_visitors: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name.trim() || !f.start_date || !f.end_date) return toast.error("Name + dates required");
    setBusy(true);
    try {
      const payload = { ...f, total_stall_capacity: f.total_stall_capacity ? Number(f.total_stall_capacity) : null, expected_visitors: f.expected_visitors ? Number(f.expected_visitors) : null };
      const { data } = await api.post(`/workspaces/${ws}/organizer/expos`, payload);
      toast.success("Expo created");
      onDone?.(data.id);
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={v => !busy && !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle>New managed expo</DialogTitle></DialogHeader>
        <div className="space-y-2">
          <div><Label className="text-xs">Name *</Label><Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} className="h-9 rounded-none" data-testid="org-expo-form-name" /></div>
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2"><Label className="text-xs">City</Label><Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} className="h-9 rounded-none" /></div>
            <div><Label className="text-xs">Country</Label><Input maxLength={3} value={f.country_code} onChange={e => setF({ ...f, country_code: e.target.value.toUpperCase() })} className="h-9 rounded-none uppercase font-mono" /></div>
          </div>
          <div><Label className="text-xs">Venue</Label><Input value={f.venue_name} onChange={e => setF({ ...f, venue_name: e.target.value })} className="h-9 rounded-none" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Start date *</Label><Input type="date" value={f.start_date} onChange={e => setF({ ...f, start_date: e.target.value })} className="h-9 rounded-none" /></div>
            <div><Label className="text-xs">End date *</Label><Input type="date" value={f.end_date} onChange={e => setF({ ...f, end_date: e.target.value })} className="h-9 rounded-none" /></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Stalls</Label><Input type="number" value={f.total_stall_capacity} onChange={e => setF({ ...f, total_stall_capacity: e.target.value })} className="h-9 rounded-none" /></div>
            <div><Label className="text-xs">Expected visitors</Label><Input type="number" value={f.expected_visitors} onChange={e => setF({ ...f, expected_visitors: e.target.value })} className="h-9 rounded-none" /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={f.status} onValueChange={v => setF({ ...f, status: v })}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="org-expo-form-submit">{busy ? "Creating…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Expo Detail (basic — floor plans deferred) ───────────

function ExpoDetail() {
  const { id } = useParams();
  const { activeWorkspaceId: ws } = useAuth();
  const navigate = useNavigate();
  const [expo, setExpo] = useState(null);
  const [tab, setTab] = useState("exhibitors");
  const [parts, setParts] = useState([]);
  const [regs, setRegs] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);

  const load = useCallback(async () => {
    const [e, p, r] = await Promise.all([
      api.get(`/workspaces/${ws}/organizer/expos/${id}`),
      api.get(`/workspaces/${ws}/organizer/expos/${id}/participations`),
      api.get(`/workspaces/${ws}/organizer/expos/${id}/registrations`),
    ]);
    setExpo(e.data); setParts(p.data || []); setRegs(r.data || []);
  }, [ws, id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (v) => { await api.patch(`/workspaces/${ws}/organizer/expos/${id}`, { status: v }); load(); };
  const setPartStatus = async (pid, v) => { await api.patch(`/workspaces/${ws}/organizer/participations/${pid}`, { status: v }); load(); };
  const setRegStatus = async (rid, v) => { await api.patch(`/workspaces/${ws}/organizer/registrations/${rid}`, { status: v }); load(); };

  if (!expo) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const stallsPct = expo.total_stall_capacity ? Math.round(100 * expo.stalls_sold / expo.total_stall_capacity) : null;

  return (
    <div className="p-6 space-y-4" data-testid="org-expo-detail">
      <Button variant="ghost" size="sm" className="rounded-none" onClick={() => navigate("/app/expo-organizer/expos")}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
      <PageHeader title={expo.name} description={`${expo.city || "—"}${expo.country_code ? `, ${expo.country_code}` : ""} · ${expo.venue_name || ""} · ${expo.start_date} → ${expo.end_date}`} actions={
        <Select value={expo.status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-36 rounded-none"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(STATUS_COLORS).map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      } />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi icon={<Building2 className="w-4 h-4 text-neutral-600" />} label="Stalls sold" value={`${expo.stalls_sold}/${expo.total_stall_capacity || "—"}${stallsPct != null ? ` · ${stallsPct}%` : ""}`} />
        <Kpi icon={<Ticket className="w-4 h-4 text-purple-500" />} label="Registrations" value={`${expo.registrations_count}/${expo.expected_visitors ? (expo.expected_visitors / 1000).toFixed(0) + "k" : "—"}`} />
        <Kpi icon={<DollarSign className="w-4 h-4 text-emerald-600" />} label="Revenue booked" value={parts.filter(p => ["booked", "paid"].includes(p.status)).reduce((s, p) => s + (p.amount || 0), 0).toLocaleString()} />
        <Kpi icon={<Globe2 className="w-4 h-4 text-blue-500" />} label="Days" value={Math.ceil((new Date(expo.end_date) - new Date(expo.start_date)) / 86400000) + 1} />
      </div>

      <div className="flex gap-1 border-b">
        {[["exhibitors", `Exhibitors (${parts.length})`], ["visitors", `Visitors (${regs.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm border-b-2 ${tab === k ? "border-primary text-primary" : "border-transparent text-neutral-600"}`} data-testid={`org-expo-tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "exhibitors" && (
        <div className="border">
          <div className="p-2 border-b flex items-center justify-between bg-neutral-50">
            <div className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Booked exhibitors ({parts.length})</div>
            <Button size="sm" className="rounded-none h-7" onClick={() => setLinkOpen(true)} data-testid="org-link-exhibitor"><Plus className="w-3 h-3 mr-1" /> Add exhibitor</Button>
          </div>
          {parts.length === 0 ? <div className="p-6 text-center text-xs text-muted-foreground italic">No exhibitors added to this expo yet.</div> : (
            <table className="w-full text-xs">
              <thead className="bg-neutral-50/50 text-[10px] uppercase tracking-widest text-neutral-500"><tr><th className="text-left p-2">Company</th><th className="text-left p-2">Contact</th><th className="text-left p-2">Stall</th><th className="text-left p-2">Amount</th><th className="text-left p-2">Status</th></tr></thead>
              <tbody>
                {parts.map(p => (
                  <tr key={p.id} className="border-t" data-testid={`org-part-${p.id}`}>
                    <td className="p-2 font-medium">{p.exhibitor?.company_name || "—"}</td>
                    <td className="p-2 text-muted-foreground">{p.exhibitor?.primary_contact_name} · {p.exhibitor?.primary_contact_email}</td>
                    <td className="p-2 font-mono">{p.stall_number || "—"}</td>
                    <td className="p-2 font-mono">{p.amount ? p.amount.toLocaleString() : "—"} {p.currency || ""}</td>
                    <td className="p-2">
                      <Select value={p.status} onValueChange={v => setPartStatus(p.id, v)}>
                        <SelectTrigger className="h-7 w-28 rounded-none text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{PART_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "visitors" && (
        <div className="border">
          <div className="p-2 border-b flex items-center justify-between bg-neutral-50">
            <div className="text-xs font-semibold uppercase tracking-widest text-neutral-600">Registered visitors ({regs.length})</div>
            <Button size="sm" className="rounded-none h-7" onClick={() => setRegOpen(true)} data-testid="org-add-visitor"><Plus className="w-3 h-3 mr-1" /> Add visitor</Button>
          </div>
          {regs.length === 0 ? <div className="p-6 text-center text-xs text-muted-foreground italic">No visitor registrations yet.</div> : (
            <table className="w-full text-xs">
              <thead className="bg-neutral-50/50 text-[10px] uppercase tracking-widest text-neutral-500"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Company</th><th className="text-left p-2">Ticket</th><th className="text-left p-2">Registered</th><th className="text-left p-2">Status</th></tr></thead>
              <tbody>
                {regs.map(r => (
                  <tr key={r.id} className="border-t" data-testid={`org-reg-${r.id}`}>
                    <td className="p-2 font-medium">{r.visitor?.name}</td>
                    <td className="p-2 text-muted-foreground">{r.visitor?.company || "—"}</td>
                    <td className="p-2">{r.ticket_tier || "—"} {r.ticket_price ? `· ${r.ticket_price}` : ""}</td>
                    <td className="p-2 text-[11px] text-muted-foreground">{r.created_at?.slice(0, 10)}</td>
                    <td className="p-2">
                      <Select value={r.status} onValueChange={v => setRegStatus(r.id, v)}>
                        <SelectTrigger className="h-7 w-28 rounded-none text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{REG_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {linkOpen && <LinkExhibitorDialog ws={ws} expoId={id} onClose={() => setLinkOpen(false)} onDone={() => { setLinkOpen(false); load(); }} />}
      {regOpen && <AddVisitorDialog ws={ws} expoId={id} onClose={() => setRegOpen(false)} onDone={() => { setRegOpen(false); load(); }} />}

      {/* Iter Feb 2026 R37 · Phase 2 add-ons — floor plan / tickets /
           sessions / kiosk — appended below the existing exhibitors +
           visitors tabs so it doesn't disturb the Batch-B UX. */}
      <Phase2Panel wsId={ws} expoId={id} />
    </div>
  );
}

function LinkExhibitorDialog({ ws, expoId, onClose, onDone }) {
  const [pool, setPool] = useState([]);
  const [xid, setXid] = useState("");
  const [stall, setStall] = useState("");
  const [amount, setAmount] = useState("");
  const [status, setStatus] = useState("enquiry");
  useEffect(() => { api.get(`/workspaces/${ws}/organizer/exhibitors`).then(r => setPool(r.data || [])); }, [ws]);
  const submit = async () => {
    if (!xid) return toast.error("Pick an exhibitor");
    try {
      await api.post(`/workspaces/${ws}/organizer/expos/${expoId}/participations`, { exhibitor_id: xid, status, stall_number: stall || null, amount: amount ? Number(amount) : null });
      toast.success("Exhibitor added");
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle>Add exhibitor to expo</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Exhibitor *</Label>
            <Select value={xid} onValueChange={setXid}>
              <SelectTrigger className="h-9 rounded-none" data-testid="org-link-pick"><SelectValue placeholder={pool.length ? "Pick from pool…" : "No exhibitors in pool"} /></SelectTrigger>
              <SelectContent>{pool.map(x => <SelectItem key={x.id} value={x.id}>{x.company_name}</SelectItem>)}</SelectContent>
            </Select>
            {pool.length === 0 && <div className="text-[10px] text-rose-600 mt-1">Add an exhibitor to the pool first.</div>}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Stall #</Label><Input value={stall} onChange={e => setStall(e.target.value)} className="h-9 rounded-none" /></div>
            <div><Label className="text-xs">Amount</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="h-9 rounded-none" /></div>
            <div><Label className="text-xs">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-9 rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{PART_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={submit} data-testid="org-link-submit">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddVisitorDialog({ ws, expoId, onClose, onDone }) {
  const [pool, setPool] = useState([]);
  const [vid, setVid] = useState("");
  const [tier, setTier] = useState("");
  const [price, setPrice] = useState("");
  useEffect(() => { api.get(`/workspaces/${ws}/organizer/visitors`).then(r => setPool(r.data || [])); }, [ws]);
  const submit = async () => {
    if (!vid) return toast.error("Pick a visitor");
    try {
      await api.post(`/workspaces/${ws}/organizer/expos/${expoId}/registrations`, { visitor_id: vid, ticket_tier: tier || null, ticket_price: price ? Number(price) : null });
      toast.success("Visitor registered");
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <Dialog open onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle>Register visitor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Visitor *</Label>
            <Select value={vid} onValueChange={setVid}>
              <SelectTrigger className="h-9 rounded-none"><SelectValue placeholder={pool.length ? "Pick from pool…" : "No visitors in pool"} /></SelectTrigger>
              <SelectContent>{pool.map(x => <SelectItem key={x.id} value={x.id}>{x.name} {x.email ? `· ${x.email}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Ticket tier</Label><Input value={tier} onChange={e => setTier(e.target.value)} className="h-9 rounded-none" placeholder="e.g. Business" /></div>
            <div><Label className="text-xs">Price</Label><Input type="number" value={price} onChange={e => setPrice(e.target.value)} className="h-9 rounded-none" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button><Button className="rounded-none" onClick={submit}>Register</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────── Exhibitor Pool ───────────────

function ExhibitorPool() {
  const { activeWorkspaceId: ws } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const load = useCallback(() => {
    const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
    api.get(`/workspaces/${ws}/organizer/exhibitors${params}`).then(r => setRows(r.data || []));
  }, [ws, q]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-6 space-y-4" data-testid="org-exhibitor-pool">
      <PageHeader title="Exhibitor Pool" description="Global exhibitor CRM — accessible across all your managed expos." actions={
        <Button className="rounded-none h-9" onClick={() => setOpenCreate(true)} data-testid="org-exhibitor-new"><Plus className="w-4 h-4 mr-1" /> Add exhibitor</Button>
      } />
      <Input placeholder="Search company / contact…" value={q} onChange={e => setQ(e.target.value)} className="h-8 rounded-none text-xs max-w-md" />
      <div className="border">
        {rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No exhibitors yet.</div> : (
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-500"><tr><th className="text-left p-2">Company</th><th className="text-left p-2">Industry</th><th className="text-left p-2">Contact</th><th className="text-left p-2">Bookings</th></tr></thead>
            <tbody>
              {rows.map(x => (
                <tr key={x.id} className="border-t hover:bg-neutral-50" data-testid={`org-exhibitor-${x.id}`}>
                  <td className="p-2 font-medium">{x.company_name}</td>
                  <td className="p-2 text-muted-foreground">{x.industry || "—"}</td>
                  <td className="p-2 text-muted-foreground">{x.primary_contact_name} · {x.primary_contact_email}</td>
                  <td className="p-2 font-mono">{x.bookings_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {openCreate && <QuickCreatePoolDialog ws={ws} kind="exhibitor" onClose={() => setOpenCreate(false)} onDone={() => { setOpenCreate(false); load(); }} />}
    </div>
  );
}

// ─────────────── Visitor Pool ───────────────

function VisitorPool() {
  const { activeWorkspaceId: ws } = useAuth();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const load = useCallback(() => {
    const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
    api.get(`/workspaces/${ws}/organizer/visitors${params}`).then(r => setRows(r.data || []));
  }, [ws, q]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="p-6 space-y-4" data-testid="org-visitor-pool">
      <PageHeader title="Visitor Pool" description="Global visitor CRM — track registrations across every expo." actions={
        <Button className="rounded-none h-9" onClick={() => setOpenCreate(true)} data-testid="org-visitor-new"><Plus className="w-4 h-4 mr-1" /> Add visitor</Button>
      } />
      <Input placeholder="Search name / email / company…" value={q} onChange={e => setQ(e.target.value)} className="h-8 rounded-none text-xs max-w-md" />
      <div className="border">
        {rows.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">No visitors yet.</div> : (
          <table className="w-full text-xs">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-widest text-neutral-500"><tr><th className="text-left p-2">Name</th><th className="text-left p-2">Email</th><th className="text-left p-2">Company</th><th className="text-left p-2">Events attended</th></tr></thead>
            <tbody>
              {rows.map(v => (
                <tr key={v.id} className="border-t hover:bg-neutral-50" data-testid={`org-visitor-${v.id}`}>
                  <td className="p-2 font-medium">{v.name}</td>
                  <td className="p-2 text-muted-foreground">{v.email || "—"}</td>
                  <td className="p-2">{v.company || "—"}</td>
                  <td className="p-2 font-mono">{v.events_attended_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {openCreate && <QuickCreatePoolDialog ws={ws} kind="visitor" onClose={() => setOpenCreate(false)} onDone={() => { setOpenCreate(false); load(); }} />}
    </div>
  );
}

function QuickCreatePoolDialog({ ws, kind, onClose, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      if (kind === "exhibitor") {
        if (!f.company_name?.trim()) return toast.error("Company name required");
        await api.post(`/workspaces/${ws}/organizer/exhibitors`, f);
      } else {
        if (!f.name?.trim()) return toast.error("Name required");
        await api.post(`/workspaces/${ws}/organizer/visitors`, f);
      }
      toast.success("Saved");
      onDone?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={v => !busy && !v && onClose()}>
      <DialogContent className="max-w-md rounded-none">
        <DialogHeader><DialogTitle>New {kind}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {kind === "exhibitor" ? (
            <>
              <div><Label className="text-xs">Company name *</Label><Input value={f.company_name || ""} onChange={e => setF({ ...f, company_name: e.target.value })} className="h-9 rounded-none" data-testid="org-pool-company" /></div>
              <div><Label className="text-xs">Industry</Label><Input value={f.industry || ""} onChange={e => setF({ ...f, industry: e.target.value })} className="h-9 rounded-none" /></div>
              <div><Label className="text-xs">Website</Label><Input value={f.website || ""} onChange={e => setF({ ...f, website: e.target.value })} className="h-9 rounded-none" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Contact name</Label><Input value={f.primary_contact_name || ""} onChange={e => setF({ ...f, primary_contact_name: e.target.value })} className="h-9 rounded-none" /></div>
                <div><Label className="text-xs">Contact email</Label><Input type="email" value={f.primary_contact_email || ""} onChange={e => setF({ ...f, primary_contact_email: e.target.value })} className="h-9 rounded-none" /></div>
              </div>
              <div><Label className="text-xs">Contact phone</Label><Input value={f.primary_contact_phone || ""} onChange={e => setF({ ...f, primary_contact_phone: e.target.value })} className="h-9 rounded-none" /></div>
            </>
          ) : (
            <>
              <div><Label className="text-xs">Name *</Label><Input value={f.name || ""} onChange={e => setF({ ...f, name: e.target.value })} className="h-9 rounded-none" data-testid="org-pool-name" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Email</Label><Input type="email" value={f.email || ""} onChange={e => setF({ ...f, email: e.target.value })} className="h-9 rounded-none" /></div>
                <div><Label className="text-xs">Phone</Label><Input value={f.phone || ""} onChange={e => setF({ ...f, phone: e.target.value })} className="h-9 rounded-none" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Company</Label><Input value={f.company || ""} onChange={e => setF({ ...f, company: e.target.value })} className="h-9 rounded-none" /></div>
                <div><Label className="text-xs">Title</Label><Input value={f.title || ""} onChange={e => setF({ ...f, title: e.target.value })} className="h-9 rounded-none" /></div>
              </div>
            </>
          )}
        </div>
        <DialogFooter><Button variant="outline" className="rounded-none" onClick={onClose} disabled={busy}>Cancel</Button><Button className="rounded-none" onClick={submit} disabled={busy} data-testid="org-pool-submit">{busy ? "Saving…" : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
